import ErrorDialog from "@/components/ErrorDialog"
import FollowButton from "@/components/FollowButton"
import MediaFrame from "@/components/MediaFrame"
import UsernameMark from "@/components/UsernameMark"
import { useAuth } from "@/hooks/useAuth"
import { useAuthModal } from "@/hooks/useAuthModal"
import { compactCount, isBoosted, parseInterests } from "@/lib/culture"
import { isLivePost, isSeedPost, mediaLabel, postPath, postRecordId, rankForYou, samePostId, timeAgo, toPublicPost } from "@/lib/filters"
import { lumi } from "@/lib/lumi"
import { fetchPostById } from "@/lib/posts"
import { isSeedProfile, isSeedUserId, publicProfilesFor } from "@/lib/profile"
import { privacyPublicProfiles, privacyUpdatePost } from "@/lib/privacy"
import { listBlockedIds, notify } from "@/lib/social"
import type { Bookmark, Comment, Follow, Like, Post, Profile } from "@/types"
import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"

export default function FeedPage() {
  const { user, isAuthenticated } = useAuth()
  const { openAuth } = useAuthModal()
  const navigate = useNavigate()
  const { postId } = useParams()
  const location = useLocation()
  const startPostId = postRecordId(decodeURIComponent(postId || ""))
  const focused = Boolean(startPostId)
  const statePost = (location.state as { post?: Post } | null)?.post
  const scroller = useRef<HTMLDivElement>(null)
  const [tab, setTab] = useState<"foryou" | "following">("foryou")
  const [posts, setPosts] = useState<Post[]>([])
  const [focusedPost, setFocusedPost] = useState<Post | null>(null)
  const [focusedReady, setFocusedReady] = useState(!startPostId)
  const [likes, setLikes] = useState<Like[]>([])
  const [comments, setComments] = useState<Comment[]>([])
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([])
  const [follows, setFollows] = useState<Follow[]>([])
  const [profiles, setProfiles] = useState<Record<string, Profile>>({})
  const [blockedIds, setBlockedIds] = useState<string[]>([])
  const [interests, setInterests] = useState<string[]>([])
  const [prefCity, setPrefCity] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [active, setActive] = useState(0)
  const [muted, setMuted] = useState(true)
  const [more, setMore] = useState<string>("")
  const [shareId, setShareId] = useState("")
  const [commentPost, setCommentPost] = useState<Post | null>(null)
  const [commentText, setCommentText] = useState("")
  const [replyTo, setReplyTo] = useState<Comment | null>(null)
  const [tipPost, setTipPost] = useState<Post | null>(null)

  const load = async () => {
    try {
      const [postRes, likeRes, commentRes, followRes, bookmarkRes, blockRes, mineRes] = await Promise.all([
        lumi.entities.posts.list({ sort: { createdAt: -1 }, limit: 400 }),
        lumi.entities.likes.list({ limit: 400 }),
        lumi.entities.comments.list({ sort: { createdAt: 1 }, limit: 400 }),
        lumi.entities.follows.list({ limit: 200 }),
        user?.userId ? lumi.entities.bookmarks.list({ filter: { userId: user.userId }, limit: 200 }) : Promise.resolve({ list: [] }),
        listBlockedIds(user?.userId),
        user?.userId ? lumi.entities.profiles.list({ filter: { userId: user.userId }, limit: 1 }) : Promise.resolve({ list: [] }),
      ])
      const allPosts = ((postRes?.list || []) as Post[])
        .map((post) => toPublicPost({ ...post, _id: postRecordId(post) }, user?.userId))
        .filter((post) => post._id && isLivePost(post) && !isSeedPost(post))
      const nextLikes = ((likeRes?.list || []) as Like[]).filter((item) => !isSeedUserId(item.userId) && !blockRes.includes(item.userId))
      const nextComments = ((commentRes?.list || []) as Comment[]).filter((item) => !isSeedUserId(item.authorId) && !blockRes.includes(item.authorId))
      setPosts(allPosts)
      setLikes(nextLikes)
      setComments(nextComments)
      setFollows(((followRes?.list || []) as Follow[]).filter((item) => !isSeedUserId(item.followerId) && !isSeedUserId(item.followingId)))
      setBookmarks(((bookmarkRes?.list || []) as Bookmark[]).filter((item) => item.userId === user?.userId))
      setBlockedIds(blockRes)
      const mine = ((mineRes?.list || []) as Profile[]).find((item) => item.userId === user?.userId)
      setInterests(parseInterests(mine?.interests))
      setPrefCity((mine?.city || "").trim())
      const neededIds = Array.from(new Set([
        ...allPosts.map((post) => post.authorId),
        ...nextComments.map((item) => item.authorId),
      ].filter(Boolean))).slice(0, 200)
      const publicList = neededIds.length ? await privacyPublicProfiles({ userIds: neededIds }) : []
      const map: Record<string, Profile> = {}
      publicProfilesFor(publicList)
        .filter((profile) => !isSeedProfile(profile))
        .forEach((profile) => {
          map[profile.userId] = profile
        })
      setProfiles(map)
    } catch (err) {
      console.error("Feed load failed:", err)
      setError("Could not load the feed.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [user?.userId])

  useEffect(() => {
    let cancelled = false
    if (!startPostId) {
      setFocusedPost(null)
      setFocusedReady(true)
      return
    }
    const fromState = statePost && samePostId(statePost, startPostId)
      ? { ...statePost, _id: startPostId }
      : null
    setFocusedPost(fromState)
    setFocusedReady(false)
    fetchPostById(startPostId)
      .then((post) => {
        if (cancelled) return
        const hit = post && samePostId(post, startPostId) ? { ...post, _id: startPostId } : fromState
        setFocusedPost(hit)
        setFocusedReady(true)
      })
      .catch((err) => {
        console.error("Focused post load failed:", err)
        if (cancelled) return
        setFocusedPost(fromState)
        setFocusedReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [startPostId])

  const followingIds = useMemo(() => new Set(follows.filter((item) => item.followerId === user?.userId).map((item) => item.followingId)), [follows, user?.userId])
  const ranked = useMemo(() => rankForYou(posts, likes, comments, { interests, city: prefCity }), [posts, likes, comments, interests, prefCity])
  const feed = focused || tab === "foryou" ? ranked : ranked.filter((post) => followingIds.has(post.authorId))
  const blocked = useMemo(() => new Set(blockedIds), [blockedIds])
  const visible = useMemo(() => {
    const next = feed.filter((post) => {
      if (blocked.has(post.authorId) || blocked.has(post.ownerId || "")) return false
      if (startPostId && samePostId(post._id, startPostId)) return true
      return !(post.hiddenBy || "").split(",").includes(user?.userId || "none")
    })
    if (!startPostId) return next
    const fromFeed = next.find((post) => samePostId(post, startPostId))
    const focusedBlocked = focusedPost && (blocked.has(focusedPost.authorId) || blocked.has(focusedPost.ownerId || ""))
    const hit = fromFeed || (!focusedBlocked && focusedPost && samePostId(focusedPost, startPostId) && isLivePost(focusedPost) && !isSeedPost(focusedPost) ? { ...focusedPost, _id: startPostId } : null)
    if (!hit) return []
    return [hit, ...next.filter((post) => !samePostId(post, startPostId))]
  }, [feed, blocked, user?.userId, startPostId, focusedPost])

  useEffect(() => {
    const root = scroller.current
    if (!root) return
    const cards = Array.from(root.querySelectorAll("[data-feed-card]"))
    const observer = new IntersectionObserver(
      (entries) => {
        const best = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (!best) return
        const index = Number((best.target as HTMLElement).dataset.index || 0)
        if (focused && scroller.current && scroller.current.scrollTop < 24) {
          setActive(0)
          return
        }
        setActive(index)
      },
      { root, threshold: 0.65 },
    )
    cards.forEach((card) => observer.observe(card))
    return () => observer.disconnect()
  }, [visible.length, tab, startPostId])

  useEffect(() => {
    if (!focused) return
    setActive(0)
    scroller.current?.scrollTo({ top: 0 })
  }, [focused, startPostId, visible.length])

  const needAuth = () => {
    if (!isAuthenticated || !user?.userId) {
      openAuth()
      return true
    }
    return false
  }

  const toggleLike = async (post: Post) => {
    if (needAuth()) return
    const mine = likes.find((item) => item.postId === post._id && item.userId === user?.userId)
    try {
      if (mine?._id) {
        await lumi.entities.likes.delete(mine._id)
      } else {
        const now = new Date().toISOString()
        await lumi.entities.likes.create({ postId: post._id, userId: user?.userId, creator: user?.userId, createdAt: now, updatedAt: now })
        await notify({ recipientId: post.authorId, actorId: user?.userId, type: "like", postId: post._id })
      }
      await load()
    } catch (err) {
      console.error("Like failed:", err)
    }
  }

  const toggleBookmark = async (post: Post) => {
    if (needAuth()) return
    const mine = bookmarks.find((item) => item.postId === post._id && item.userId === user?.userId)
    try {
      if (mine?._id) await lumi.entities.bookmarks.delete(mine._id)
      else {
        const now = new Date().toISOString()
        await lumi.entities.bookmarks.create({ postId: post._id, userId: user?.userId, creator: user?.userId, createdAt: now, updatedAt: now })
      }
      await load()
    } catch (err) {
      console.error("Bookmark failed:", err)
    }
  }

  const sendComment = async () => {
    if (!commentPost || needAuth() || !commentText.trim()) return
    const now = new Date().toISOString()
    try {
      await lumi.entities.comments.create({
        postId: commentPost._id,
        authorId: user?.userId,
        text: commentText.trim(),
        parentId: replyTo?._id || "",
        likeCount: 0,
        creator: user?.userId,
        createdAt: now,
        updatedAt: now,
      })
      await notify({
        recipientId: replyTo?.authorId || commentPost.authorId,
        actorId: user?.userId,
        type: replyTo ? "reply" : "comment",
        postId: commentPost._id,
        text: commentText.trim(),
      })
      setCommentText("")
      setReplyTo(null)
      await load()
    } catch (err) {
      console.error("Comment failed:", err)
    }
  }

  const hidePost = async (post: Post) => {
    if (needAuth()) return
    const hidden = new Set((post.hiddenBy || "").split(",").filter(Boolean))
    hidden.add(user?.userId || "")
    try {
      await privacyUpdatePost(post._id, "hide")
      await load()
    } catch (err) {
      console.error("Hide failed:", err)
    }
  }

  const share = async (post: Post) => {
    const url = `${window.location.origin}${postPath(post._id)}`
    try {
      if (navigator.share) await navigator.share({ title: "KLIPO", text: post.caption, url })
      else {
        await navigator.clipboard.writeText(url)
        setShareId(post._id)
        window.setTimeout(() => setShareId(""), 1400)
      }
    } catch (err) {
      console.error("Share failed:", err)
    }
  }

  const opened = focused ? visible[0] : null
  const waitingForOpened = focused && !focusedReady && !opened

  if (loading || waitingForOpened) {
    return (
      <div className="space-y-3 px-4 py-6">
        {[0, 1].map((item) => <div key={item} className="h-[70vh] animate-pulse rounded-3xl bg-[#141414]" />)}
      </div>
    )
  }

  if (focused && visible.length === 0) {
    return (
      <div className="px-5 py-16 text-center">
        <h1 className="font-[Syne] text-2xl font-bold">Post unavailable</h1>
        <p className="mt-2 text-sm text-[#8A8A8A]">This photo or video is gone, hidden, or the link is wrong.</p>
        <Link to="/explore" className="mt-6 inline-block rounded-xl bg-[#00FF66] px-5 py-3 text-sm font-semibold text-black">Explore</Link>
      </div>
    )
  }

  if (visible.length === 0) {
    const suggested = Object.values(profiles).filter((person) => !blocked.has(person.userId)).slice(0, 6)
    return (
      <div className="px-5 py-10 text-center">
        <div className="mx-auto h-24 w-24 rounded-3xl border border-[#1F1F1F] bg-[#0B0B0B]" />
        <h1 className="mt-6 font-[Syne] text-2xl font-bold">Follow people or post the first clip</h1>
        <p className="mt-2 text-sm text-[#8A8A8A]">Home should feel full of blocks, cars, food, and night clips.</p>
        <div className="mt-6 grid grid-cols-2 gap-2">
          <Link to="/create" className="rounded-xl bg-[#00FF66] py-3 text-sm font-semibold text-black">Create</Link>
          <Link to="/explore" className="rounded-xl border border-[#1F1F1F] py-3 text-sm">Explore</Link>
        </div>
        {suggested.length > 0 ? (
          <div className="mt-8 text-left">
            <p className="text-xs uppercase tracking-[0.18em] text-[#8A8A8A]">Suggested</p>
            <div className="mt-3 space-y-2">
              {suggested.map((person) => (
                <Link key={person.userId} to={`/u/${person.userId}`} className="flex items-center justify-between rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-3 py-2">
                  <span className="text-sm"><UsernameMark username={person.username} userId={person.userId} /></span>
                  <span className="text-xs text-[#8A8A8A]">{person.city || person.bio || "Creator"}</span>
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="relative">
      {focused ? null : (
        <div className="absolute left-0 right-0 top-0 z-20 flex justify-center gap-5 pt-1 text-sm font-semibold">
          {(["foryou", "following"] as const).map((item) => (
            <button key={item} type="button" onClick={() => setTab(item)} className={tab === item ? "text-white" : "text-[#8A8A8A]"}>
              {item === "foryou" ? "For You" : "Following"}
              {tab === item ? <span className="mx-auto mt-1 block h-0.5 w-8 bg-[#00FF66]" /> : null}
            </button>
          ))}
        </div>
      )}

      {!focused && visible.length < 5 ? (
        <div className="absolute left-3 right-3 top-10 z-20 rounded-2xl border border-[#1F1F1F] bg-black/80 p-3 backdrop-blur">
          <p className="text-sm font-semibold">Keep the feed moving</p>
          <p className="mt-1 text-xs text-[#8A8A8A]">Follow people or post another clip so Home never looks empty.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to="/create" className="rounded-xl bg-[#00FF66] py-2 text-center text-xs font-semibold text-black">Create</Link>
            <Link to="/explore" className="rounded-xl border border-[#1F1F1F] py-2 text-center text-xs">Explore</Link>
          </div>
        </div>
      ) : null}

      <div ref={scroller} className="snap-feed mx-auto h-[calc(100dvh-7.5rem)] w-full max-w-xl overflow-y-auto sm:max-w-2xl lg:max-w-4xl">
        {visible.map((post, index) => {
          const author = profiles[post.authorId]
          const likeCount = likes.filter((item) => item.postId === post._id).length
          const commentCount = comments.filter((item) => item.postId === post._id).length
          const liked = likes.some((item) => item.postId === post._id && item.userId === user?.userId)
          const saved = bookmarks.some((item) => item.postId === post._id && item.userId === user?.userId)
          const captionOpen = more === post._id
          return (
            <article key={`${post._id}-${index}`} data-feed-card data-index={index} className="relative h-[calc(100dvh-7.5rem)] w-full overflow-hidden bg-black">
              <MediaFrame key={postRecordId(post) || post._id} post={post} autoPlay={index === active} muted={muted} attemptUnmute={!muted} controls={false} className="h-full w-full" />
              <div className="absolute bottom-4 left-3 right-16 z-10 space-y-2">
                <div className="flex items-center gap-2">
                  <Link to={`/u/${post.authorId}`} className="h-10 w-10 overflow-hidden rounded-full border border-white/20 bg-[#141414]">
                    {author?.avatarUrl ? <img src={author.avatarUrl} alt="" className="h-full w-full object-cover" /> : null}
                  </Link>
                  <UsernameMark username={author?.username} displayName={author?.displayName} userId={post.authorId} className="text-sm font-semibold" />
                  <FollowButton targetUserId={post.authorId} following={follows.find((item) => item.followerId === user?.userId && item.followingId === post.authorId) || null} onChanged={load} />
                </div>
                <p className={`text-sm ${captionOpen ? "" : "line-clamp-2"}`}>{post.caption}</p>
                {post.caption.length > 70 ? (
                  <button type="button" onClick={() => setMore(captionOpen ? "" : post._id)} className="text-xs text-[#8A8A8A]">{captionOpen ? "less" : "more"}</button>
                ) : null}
                <p className="text-xs text-[#8A8A8A]">{timeAgo(post.createdAt)} · {mediaLabel(post)}{post.city ? ` · ${post.city}` : ""}{isBoosted(post.boostedUntil) ? " · Boosted" : ""}</p>
                <p className="inline-flex rounded-full border border-white/20 bg-black/40 px-3 py-1 text-xs">{post.audioTitle || "Original audio"}</p>
              </div>
              <div className="absolute bottom-6 right-2 z-10 flex flex-col items-center gap-3">
                <RailButton label={muted ? "Muted" : "Sound"} active={!muted} onClick={() => setMuted((prev) => !prev)} name={muted ? "Unmute" : "Mute"}>
                  <SoundIcon muted={muted} />
                </RailButton>
                <RailButton label={compactCount(likeCount)} active={liked} onClick={() => toggleLike(post)} name="Like">
                  <HeartIcon filled={liked} />
                </RailButton>
                <RailButton label={compactCount(commentCount)} onClick={() => setCommentPost(post)} name="Comment">
                  <CommentIcon />
                </RailButton>
                <RailButton label={compactCount(Number(post.remixCount) || 0)} onClick={() => navigate(`/create?remix=${post._id}`)} name="Remix">
                  <RemixIcon />
                </RailButton>
                <RailButton label={saved ? "Saved" : "Save"} active={saved} onClick={() => toggleBookmark(post)} name="Save">
                  <SaveIcon filled={saved} />
                </RailButton>
                <RailButton label={shareId === post._id ? "Copied" : "Share"} onClick={() => share(post)} name="Share">
                  <ShareIcon />
                </RailButton>
                <RailButton label="Tip" onClick={() => setTipPost(post)} name="Tip">
                  <TipIcon />
                </RailButton>
                <RailButton label="Hide" onClick={() => hidePost(post)} name="Hide">
                  <HideIcon />
                </RailButton>
              </div>
            </article>
          )
        })}
      </div>

      {commentPost ? (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70" onClick={() => setCommentPost(null)}>
          <div className="flex h-[70vh] w-full max-w-2xl flex-col rounded-t-3xl bg-[#0B0B0B] p-4" onClick={(event) => event.stopPropagation()}>
            <h2 className="shrink-0 text-sm font-semibold">Comments</h2>
            <div className="mt-3 min-h-0 flex-1 space-y-3 overflow-y-auto">
              {comments.filter((item) => item.postId === commentPost._id && !item.parentId).map((comment) => (
                <div key={comment._id}>
                  <p className="text-sm"><UsernameMark username={profiles[comment.authorId]?.username} userId={comment.authorId} className="mr-2 font-semibold" />{comment.text}</p>
                  <button type="button" className="text-[11px] text-[#8A8A8A]" onClick={() => setReplyTo(comment)}>Reply</button>
                  {comments.filter((item) => item.parentId === comment._id).map((reply) => (
                    <p key={reply._id} className="ml-4 mt-1 text-sm text-[#A8A8A8]"><UsernameMark username={profiles[reply.authorId]?.username} userId={reply.authorId} className="mr-2 font-semibold" />{reply.text}</p>
                  ))}
                </div>
              ))}
            </div>
            <div className="mt-4 flex shrink-0 gap-2">
              <input value={commentText} onChange={(event) => setCommentText(event.target.value)} placeholder={replyTo ? `Reply to @${profiles[replyTo.authorId]?.username || "user"}` : "Add a comment"} className="w-full rounded-xl border border-[#1F1F1F] bg-black px-3 py-2 text-sm outline-none" />
              <button type="button" onClick={sendComment} className="rounded-xl bg-[#00FF66] px-3 text-sm font-semibold text-black">Send</button>
            </div>
          </div>
        </div>
      ) : null}

      {tipPost ? (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70" onClick={() => setTipPost(null)}>
          <div className="w-full max-w-2xl rounded-t-3xl bg-[#0B0B0B] p-5" onClick={(event) => event.stopPropagation()}>
            <h2 className="font-semibold">Tip this clip</h2>
            <p className="mt-1 text-sm text-[#8A8A8A]">Send 1, 5, or 10 $KLIPO. Connect a wallet in Wallet if you have not yet.</p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[1, 5, 10].map((amount) => (
                <Link key={amount} to="/wallet" className="rounded-xl bg-[#141414] py-3 text-center text-sm">{amount} $KLIPO</Link>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {error ? <ErrorDialog message={error} onClose={() => setError("")} /> : null}
    </div>
  )
}

function RailButton({
  label,
  children,
  onClick,
  active,
  name,
}: {
  label: string
  children: React.ReactNode
  onClick: () => void
  active?: boolean
  name: string
}) {
  return (
    <button type="button" onClick={onClick} aria-label={name} className="flex w-12 flex-col items-center gap-1">
      <span
        className={`flex h-11 w-11 items-center justify-center rounded-full border shadow-[0_0_12px_rgba(0,255,102,0.28)] ${active ? "border-[#00FF66] bg-[#00FF66] text-black" : "border-[#00C805]/70 bg-black/70 text-[#00FF66]"}`}
      >
        {children}
      </span>
      <span className={`text-[11px] font-semibold leading-none ${active ? "text-[#00FF66]" : "text-white"}`}>{label}</span>
    </button>
  )
}

function SoundIcon({ muted }: { muted?: boolean }) {
  return muted ? (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 10.5v3h3.2L13 17.8V6.2L8.2 10.5H5Z" />
      <path d="m16 10 4 4M20 10l-4 4" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 10.5v3h3.2L13 17.8V6.2L8.2 10.5H5Z" />
      <path d="M16.2 9.2a4 4 0 0 1 0 5.6M18.4 7.2a7 7 0 0 1 0 9.6" />
    </svg>
  )
}

function HeartIcon({ filled }: { filled?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8">
      <path d="M12 20s-7-4.4-7-9.2A3.8 3.8 0 0 1 12 7.6a3.8 3.8 0 0 1 7 3.2C19 15.6 12 20 12 20Z" />
    </svg>
  )
}

function CommentIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 6.5h14v9.2H9.2L5 19.5V6.5Z" />
    </svg>
  )
}

function RemixIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M7 7h7a4 4 0 0 1 4 4v1" />
      <path d="M7 7 9.5 4.5M7 7l2.5 2.5" />
      <path d="M17 17H10a4 4 0 0 1-4-4v-1" />
      <path d="M17 17 14.5 19.5M17 17l-2.5-2.5" />
    </svg>
  )
}

function SaveIcon({ filled }: { filled?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8">
      <path d="M7 5.5h10v13l-5-3.2-5 3.2v-13Z" />
    </svg>
  )
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="18" cy="6" r="2.2" />
      <circle cx="6" cy="12" r="2.2" />
      <circle cx="18" cy="18" r="2.2" />
      <path d="M8 11.2 16 7.2M8 12.8l8 4" />
    </svg>
  )
}

function TipIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 5v14" />
      <path d="M15.5 8.2c0-1.5-1.6-2.5-3.5-2.5S8.5 6.7 8.5 8.2 10 10.6 12 10.6s3.5.8 3.5 2.4-1.6 2.5-3.5 2.5-3.5-1-3.5-2.5" />
    </svg>
  )
}

function HideIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
      <circle cx="6" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="18" cy="12" r="1.6" />
    </svg>
  )
}
